import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import { MainSection } from '@/components/layout/MainSection';
import { CountryLeaguesGroup } from '@/components/CountryLeaguesGroup';
import { LeagueRow } from '@/components/LeagueRow';
import { SearchField } from '@/components/ui/SearchField';
import { useCountryExpansion } from '@/hooks/useCountryExpansion';
import { useCountryGroups } from '@/hooks/useCountryGroups';
import type { League } from '@/types/league';
import styles from '../LeaguesPage.module.scss';

/** Countries the user expanded, kept for the browser tab across re-mounts and reloads. */
const EXPANDED_COUNTRIES_STORAGE_KEY = 'leagues-list:expanded-countries';
/** Backend page-size cap of `GET /leagues/browsable`. */
const PAGE_SIZE = 500;

/** Every enabled league: pages through the endpoint until `total` is reached. */
async function fetchAllVisibleLeagues(): Promise<League[]> {
  const leagues: League[] = [];
  for (;;) {
    const page = (await apiClient.getVisibleLeagues({ limit: PAGE_SIZE, offset: leagues.length }))
      .data.data;
    leagues.push(...page.leagues);
    if (page.leagues.length === 0 || leagues.length >= page.total) return leagues;
  }
}

const NO_LEAGUES: League[] = [];

/**
 * `/matches/leagues` — enabled leagues as collapsible country cards
 * (collapsed by default). The whole set is fetched once (all pages) and the
 * search filters it on the client; while it is non-empty every country with
 * hits is expanded, without touching the persisted expanded set.
 */
const AllLeaguesTab: React.FC = () => {
  const { t } = useTranslation(['league', 'common']);
  const [query, setQuery] = useState('');
  const activeQuery = query.trim();

  const { data, isLoading, error } = useQuery<League[]>({
    queryKey: ['leagues'],
    queryFn: fetchAllVisibleLeagues,
  });

  const { visibleGroups } = useCountryGroups(data ?? NO_LEAGUES, activeQuery);
  const expansion = useCountryExpansion(EXPANDED_COUNTRIES_STORAGE_KEY, activeQuery);

  let body: React.ReactNode;
  if (error) {
    body = <p className={styles.error}>{t('league:errorLoadingLeagues')}</p>;
  } else if (isLoading) {
    body = null;
  } else if (visibleGroups.length === 0) {
    body = (
      <p className={styles.empty} data-testid="leagues-empty">
        {t('league:noLeaguesFound')}
      </p>
    );
  } else {
    body = (
      <div className={styles.groups}>
        {visibleGroups.map(group => (
          <CountryLeaguesGroup
            key={group.key}
            group={group}
            expanded={expansion.isExpanded(group.key)}
            onToggle={() => expansion.toggle(group.key)}
            renderRow={(league, showSeason) => (
              <LeagueRow
                name={league.name}
                logoUrl={league.logoUrl}
                season={showSeason ? league.season : undefined}
                to={`/matches/${league.id}`}
                testId={`league-row-${league.id}`}
              />
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <MainSection
      hideTitleOnMobile
      title={t('league:matchesTitle')}
      subtitle={t('league:leaguesCount', { count: data?.length ?? 0 })}
    >
      <div className={styles.wrapper}>
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder={t('league:findLeagues')}
          testId="leagues-search"
        />
        {body}
      </div>
    </MainSection>
  );
};

export default AllLeaguesTab;
